import * as React from 'react'

import { Box, Button, Card, Form, Text } from '@factorialco/deprecated-design-system'
import { AttendanceOvertimePool } from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useBulkUpdateOvertimePools } from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useBulkUpdateOvertimePools'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import type BalanceCarryover from 'modules/attendance/store/models/BalanceCarryover'

import { mapFrequencyOptionsToBalanceCarryover, updateFrequencyToEnum } from './utils'

interface Props {
  attendanceSetting: AttendanceSetting
  balanceCarryover: BalanceCarryover
  overtimePools: AttendanceOvertimePool[]
}

const CompensationPeriodCard: React.FC<Props> = ({
  attendanceSetting,
  balanceCarryover,
  overtimePools,
}) => {
  const { bulkUpdateOvertimePool, loading } = useBulkUpdateOvertimePools()
  const overtimePool = overtimePools.at(0)

  const form = useForm({
    defaultValues: {
      attendanceEmployeesSettingId: attendanceSetting.id,
      updateFrequency: updateFrequencyToEnum(
        overtimePool?.updateFrequency || balanceCarryover.resetFrequency
      ),
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await bulkUpdateOvertimePool({
        attendanceEmployeesSettingId: data.attendanceEmployeesSettingId,
        updateFrequency: updateFrequencyToEnum(data.updateFrequency),
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success && handled.data) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        form.resetForm({
          values: {
            attendanceEmployeesSettingId: attendanceSetting.id,
            updateFrequency:
              handled?.data?.overtimePools?.[0]?.updateFrequency ||
              updateFrequencyToEnum(balanceCarryover.resetFrequency),
          },
        })
      }

      return handled
    },
  })

  const updateFrequencyIsDaily = form.values.updateFrequency == 'daily'

  return (
    <Card>
      <Card.Title size="small">
        {i18n.t('time_tracking.settings.compensation.compensation_period')}
      </Card.Title>
      <Card.Description>
        {i18n.t('time_tracking.settings.compensation.compensation_period_description')}
      </Card.Description>
      <Card.Content>
        <Form onSubmit={() => form.handleSubmit()} padding={null}>
          <Box gap="s12">
            <Select
              value={form.values.updateFrequency}
              options={mapFrequencyOptionsToBalanceCarryover(balanceCarryover.resetFrequency)}
              onChange={(frequency) =>
                form.setValues({
                  ...form.values,
                  updateFrequency: updateFrequencyToEnum(frequency),
                })
              }
            />
            <Box flexDirection="row" gap="s4">
              {!updateFrequencyIsDaily && (
                <Text size="200">
                  <Text size="200" weight="bold">
                    {i18n.t('time_tracking.settings.balance.reset_frequency.note')}
                  </Text>{' '}
                  {i18n.t('time_tracking.settings.balance.reset_frequency.period_foot_note')}
                </Text>
              )}
            </Box>
            {form.dirty && (
              <Form.Buttons>
                <Button disabled={loading} submit>
                  {i18n.t('common.submit')}
                </Button>
              </Form.Buttons>
            )}
          </Box>
        </Form>
      </Card.Content>
    </Card>
  )
}

export default CompensationPeriodCard
